import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AMSListing from '../../components/listing/AMSListing';
import { ActionType, Column } from '../../types/types';
import { useToast } from '../../components/toast/ToastContext';
import { useNavigate } from 'react-router-dom';
import DeletePrompt from '../../components/DeletePrompt';
import { contactRolesApi, useDeleteRoleMutation } from '../../api/contactRoles';
import AssignedContactsPopup from './AssignedContactsPopup';

interface ContactRolesProps {
  onEditRole: (id: number, onSuccess: () => void) => void;
  refreshTrigger?: number;
}

const ContactRolesListings: React.FC<ContactRolesProps> = ({ onEditRole, refreshTrigger }) => {

  const contactRolesColumns = useMemo<Column[]>(() => [
    { id: 'id', label: 'Id', isSortable: true },
    { id: 'name', label: 'Name', isSortable: true },
    // { id: 'autoAssign', label: 'Auto Assign', isSortable: true },
    { id: 'contactRoles', label: 'Assigned Contacts', isSortable: true },
    { id: 'updatedAt', label: 'Last Changed', isSortable: true },
  ], []);

  const [assignedContactID, setAssignedContactID] = useState<number>();
  const [showAssignedContacts, setShowAssignedContacts] = useState<boolean>(false);
  const [roleGroups, setRoleGroups] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const showToast = useToast();
  const initialMount = useRef(true);

  const [deleteRole] = useDeleteRoleMutation();

  const lastParamsRef = useRef<{
    page: number;
    perPage: number;
    sort?: { field: string; direction: 'ASC' | 'DESC' };
    query?: string;
    filters: Record<string, any>;
    visibleColumns: string[];
  }>({
    page: 1,
    perPage: 10,
    filters: {},
    visibleColumns: [],
  });

  const fetchRoles = useCallback(async (
    params: {
      page: number;
      perPage: number;
      sort?: { field: string; direction: 'ASC' | 'DESC' };
      query?: string;
      filters: Record<string, any>;
      visibleColumns: string[];
    },
    onError?: (error: any) => void
  ) => {
    setLoading(true);

    try {

      const tableConfig = JSON.parse(localStorage.getItem('contact-roles') || '{}');
      const selectedDynamicColumns = tableConfig?.dynamicColumns?.length > 0 ? JSON.stringify(tableConfig?.dynamicColumns) : null;
      const response = await contactRolesApi.getContactRoles(
        params.page,
        params.perPage,
        params.sort,
        params.query,
        params.filters,
        selectedDynamicColumns ? params.visibleColumns : undefined,
      );

      setRoleGroups(response?.roles);
      setTotalItems(response?.totalCount);
    } catch (err: any) {
      showToast('Fetch Failed', 'Failed to fetch contacts. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFetchRequested = useCallback((params: {
    page: number;
    perPage: number;
    sort?: { field: string; direction: 'ASC' | 'DESC' };
    query?: string;
    filters: Record<string, any>;
    visibleColumns: string[];
  }) => {

    lastParamsRef.current = params;
    fetchRoles(params);
  }, [fetchRoles]);

  // const confirmDelete = async () => {
  //   if (!deleteId) return;
  //   const numericId = Number(deleteId);
  //   setShowDeletePrompt(false);

  //   try {
  //     await contactRolesApi.deleteRole(numericId.toString());
  //     setRoleGroups(prev => prev.filter(role => role.id !== numericId));
  //     setTotalItems(prev => prev - 1);
  //     showToast('Role Deleted', 'The selected role has been deleted.', 'success');
  //     handleFetchRequested(lastParamsRef.current);
  //   } catch {
  //     showToast('Delete Failed', 'Failed to delete role. Please try again.', 'error');
  //   }

  //   setDeleteId(null);
  // };

  // const cancelDelete = () => {
  //   setShowDeletePrompt(false);
  //   setDeleteId(null);
  // };

  const handleAction = useCallback(async (action: ActionType, id: string) => {
    const numericId = Number(id);

    // if (action === 'delete') {
    //   setDeleteId(id);
    //   setShowDeletePrompt(true);
    //   return;
    // }

    if (action === 'edit') {
      onEditRole(numericId, () => handleFetchRequested(lastParamsRef.current));
    }
    // if (action === 'revoke') {
    //   try {
    //     await contactRolesApi.revokePermissions(numericId.toString());
    //     showToast('Permissions Removed', 'The permissions of the selected role is removed.', 'success');
    //     handleFetchRequested(lastParamsRef.current);
    //   } catch {
    //     showToast('Failed', 'Failed to remove permissions of selected role. Please try again.', 'error');
    //   }

    // }
    if (action === 'delete') {
      try {
        await deleteRole(numericId).unwrap();
        showToast('Role Deleted', 'The selected role is deleted.', 'success');
        handleFetchRequested(lastParamsRef.current);
      } catch (error: any) {
        showToast('Failed', error?.data?.message || "Failed to delete role. Please try again.", 'error');
      }
    }
    if (action === 'showAssignedContacts') {
      setAssignedContactID(numericId);
      setShowAssignedContacts(true);
    }

  }, [handleFetchRequested, onEditRole]);

  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }
    // handleFetchRequested(lastParamsRef.current);
    if (refreshTrigger !== undefined) {
      handleFetchRequested(lastParamsRef.current);
    }
  }, [refreshTrigger]);

  return (
    <>
      <AMSListing
        routeKey="contact-roles"
        noDataTitle="No Roles Found"
        noDataSubtitle="Your Roles list is currently empty. Start adding new roles to stay organized."
        title="Contact Roles"
        subtitle="All contacts roles will show here"
        columns={contactRolesColumns}
        data={roleGroups}
        totalItems={totalItems}
        loading={loading}
        onAction={handleAction}
        onFetchRequested={handleFetchRequested}
        skeletonHeight={'29px'}
        hideActionColumn={false}
        noTableAction={false}
      />
      {/* {showDeletePrompt && (
        <DeletePrompt onConfirm={confirmDelete} onCancel={cancelDelete} />
      )} */}
      {showAssignedContacts && (
        <AssignedContactsPopup
          onClose={() => setShowAssignedContacts(false)}
          roleID={assignedContactID}
          onRoleDataChanged={() => handleFetchRequested(lastParamsRef.current)}
        />
      )}
    </>
  );
};

export default ContactRolesListings;